import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Membership, MembershipGiftingConfig } from '../../../../../../models/membership';
import { MyAccount } from '../../../../../../models/my-account';
import { MembershipService } from '../../../../../../services/membership/membership.service';
import { AccountService } from '../../../../../../services/account/account.service';
import { SimpleMessageModalComponent } from '../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { MembershipPurchaseType, MembershipStatus } from '../../../../../../models/membership-enums';


@Component({
  selector: 'app-membership-gifting-config-detail',
  templateUrl: './membership-gifting-config-detail.component.html',
  styleUrls: ['./membership-gifting-config-detail.component.css']
})
export class MembershipGiftingConfigDetailComponent implements OnInit, ComponentHasUnsavedChanges {
  public isNewConfig: boolean;
  public giftingConfig: MembershipGiftingConfig = {
    id: null,
    url: null,
    assigned_membership_ids: [],
    assigned_membership_names: [],
    max_future_delivery_date_days: 366,
    expiration_days: 366,
    gift_giver_terms_and_conditions: '',
    gift_recipient_terms_and_conditions: '',
    created_at: null,
    updated_at: null
  };
  public memberships: Membership[];

  public unsavedChanges = false;
  public errorMessage = '';
  public errors: any = {};

  public loadingExistingConfig = false;
  public loadingMemberships = true;
  public loadingSubmit = false;
  public loadingDelete = false;

  public configNotFound = false;
  public serverError: number = null;

  public myAccount: MyAccount;

  public MembershipStatus = MembershipStatus;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private membershipService: MembershipService,
    private accountService: AccountService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewConfig = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    if (!this.isNewConfig) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.configNotFound = true;
        this.loadingExistingConfig = false;
        return;
      }

      this.loadGiftingConfig(idInt);
    }

    this.loadMemberships();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public getFilteredMemberships(): Membership[] {
    return this.memberships.filter(m => {
      return (
        [
          MembershipStatus.draft,
          MembershipStatus.active
        ].includes(m.status) &&
        (m.purchase_type === MembershipPurchaseType.prepaid) &&
        !m.is_group
      );
    })
  }

  public membershipSelected(membership: Membership) {
    return this.giftingConfig.assigned_membership_ids.includes(membership.id);
  }

  public toggleMembership(membership: Membership) {
    if (this.membershipSelected(membership)) {
      this.giftingConfig.assigned_membership_ids.splice(
        this.giftingConfig.assigned_membership_ids.indexOf(membership.id),
        1
      );
    } else {
      this.giftingConfig.assigned_membership_ids.push(membership.id);
    }
    this.unsavedChanges = true;
  }

  public allMembershipsAreSelected() {
    for (const membership of this.getFilteredMemberships()) {
      if (!this.giftingConfig.assigned_membership_ids.includes(membership.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoMembershipsSelected() {
    this.giftingConfig.assigned_membership_ids = [];
    this.unsavedChanges = true;
  }

  public setAllMembershipsSelected() {
    this.giftingConfig.assigned_membership_ids = this.getFilteredMemberships().map(m => m.id);
    this.unsavedChanges = true;
  }

  public isLoading() {
    return (
      this.loadingExistingConfig ||
      this.loadingMemberships ||
      this.loadingSubmit ||
      this.loadingDelete
    )
  }

  public handleSubmit() {
    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<MembershipGiftingConfig>;
    if (this.isNewConfig) {
      savePromise = this.membershipService.addMembershipGiftingConfig(this.giftingConfig);
    } else {
      savePromise = this.membershipService.updateMembershipGiftingConfig(this.giftingConfig);
    }

    savePromise
      .then(savedConfig => {
        return this.membershipService
          .assignGiftingConfigToMemberships(
            savedConfig.id,
            this.giftingConfig.assigned_membership_ids
          )
          .then(() => {
            this.unsavedChanges = false;

            let successMessage: string;
            if (this.isNewConfig) {
              successMessage = 'Membership gifting config created successfully.';
            } else {
              successMessage = 'Membership gifting config updated successfully.';
            }

            this.router.navigate(
              ['/gifting-config'],
              {
                state: {
                  feedback: {
                    text: successMessage,
                    status: 'success'
                  }
                }
              }
            );
          })
          .catch(reason => {
            if (reason.status === 400) {
              this.errorMessage = reason.error;
            } else if (reason.status === 403) {
              if (this.isNewConfig) {
                this.errorMessage = 'You do not have permission to add membership gifting configurations.';
              } else {
                this.errorMessage = 'You do not have permission to modify this membership gifting configuration.';
              }
            } else {
              this.serverError = reason.status;
            }
          });
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewConfig) {
            this.errorMessage = 'You do not have permission to add membership gifting configurations.';
          } else {
            this.errorMessage = 'You do not have permission to modify this membership gifting configuration.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public handleDelete() {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Delete Membership Gifting Configuration';
    modalRef.componentInstance.messageText = 'Are you sure you want to delete ' +
      'this membership gifting config? This action cannot be undone.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingDelete = true;

      this.membershipService
        .deleteMembershipGiftingConfig(this.giftingConfig)
        .then(() => {
          this.unsavedChanges = false;

          this.router.navigate(
            ['/gifting-config'],
            {
              state: {
                feedback: {
                  text: 'Membership gifting configuration deleted.',
                  status: 'success'
                }
              }
            }
          )
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errors = reason.error;
            this.errorMessage = 'Please correct the errors shown and try again.';
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to delete this membership gifting configuration.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingDelete = false; });
    }
  }

  private loadMemberships() {
    this.loadingMemberships = true;

    this.membershipService
      .getMemberships(1, 3000, {})
      .then(result => {
        this.memberships = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.configNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingMemberships = false; });
  }

  private loadGiftingConfig(id: number) {
    this.loadingExistingConfig = true;

    this.membershipService
      .getMembershipGiftingConfig(id)
      .then(config => {
        this.giftingConfig = config;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.configNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingConfig = false; });
  }
}
